import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { API_BASE } from '../config/api'

interface PolicySection {
  title: string
  content: Array<{ subtitle: string; text: string }>
}

interface PrivacyPolicy {
  title: string
  lastUpdated: string
  introduction: string
  sections: PolicySection[]
  contactEmail?: string
}

// Minimal fallback (API is the source of truth)
const defaultPrivacy: PrivacyPolicy = {
  title: 'Privacy Policy',
  lastUpdated: '1 Jan 2026',
  introduction: '',
  sections: [],
  contactEmail: 'hello@ionicerrrrscode.com',
}

function formatLastUpdated(lastUpdated: string) {
  const date = new Date(lastUpdated)
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }
  return lastUpdated
}

export default function Privacy() {
  const [privacy, setPrivacy] = useState<PrivacyPolicy>(defaultPrivacy)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchPrivacy = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/v1/content/privacy`)
        const data = await response.json()
        if (data.success && data.data) {
          setPrivacy(data.data)
        }
      } catch (error) {
        console.error('Failed to fetch privacy policy:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchPrivacy()
  }, [])


  if (isLoading) {
    return (
      <div className="min-h-screen pt-32 pb-16" style={{ background: 'var(--bg)' }}>
        <div className="container-page max-w-4xl px-4">
          <div className="animate-pulse space-y-8">
            <div className="h-12 rounded w-64 mx-auto" style={{ background: 'var(--surface-2)' }} />
            <div className="h-6 rounded w-96 mx-auto" style={{ background: 'var(--surface-2)' }} />
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-48 rounded" style={{ background: 'var(--surface-2)' }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen pt-32 pb-16" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <Helmet>
        <title>Privacy Policy | Ionic Errrrs Code</title>
        <meta name="description" content="Read the Privacy Policy of Ionic Errrrs Code. We are committed to protecting your personal information and your right to privacy." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://ionicerrrrscode.com/privacy" />
        <meta property="og:title" content="Privacy Policy | Ionic Errrrs Code" />
        <meta property="og:url" content="https://ionicerrrrscode.com/privacy" />
      </Helmet>

      {/* Header Section */}
      <section className="pb-12 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="container-page max-w-4xl px-4 text-center">
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center text-sm font-semibold no-underline hover:underline"
              style={{ color: 'var(--text)' }}
            >
              ← Back to Home
            </Link>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-6" style={{ letterSpacing: '-0.02em' }}>
            {privacy.title}
          </h1>
          <p className="text-[1.12rem] leading-relaxed max-w-2xl mx-auto mb-4" style={{ color: 'var(--muted)' }}>
            We value your privacy and are committed to protecting your personal information.
          </p>
          <p className="text-xs font-mono uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
            Last updated: {formatLastUpdated(privacy.lastUpdated)}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container-page max-w-3xl px-4">
          <div className="space-y-12">
            {/* Introduction */}
            {privacy.introduction && (
              <div className="rounded-card border p-8" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                <p className="text-base leading-relaxed mb-0" style={{ color: 'var(--muted)' }}>
                  {privacy.introduction}
                </p>
              </div>
            )}

            {/* Sections */}
            {privacy.sections.map((section, index) => (
              <div key={index} className="rounded-card border p-8" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                <h2 className="font-display text-2xl font-bold mb-6 flex items-center" style={{ color: 'var(--text)', letterSpacing: '-0.01em' }}>
                  <span
                    className="w-8 h-8 rounded-full flex items-center justify-center mr-4 text-sm font-semibold"
                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}
                  >
                    {index + 1}
                  </span>
                  {section.title}
                </h2>
                <div className="space-y-8">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex} className="border-l pl-4" style={{ borderColor: 'var(--accent)' }}>
                      <h3 className="font-display text-[1.12rem] font-semibold mb-2" style={{ color: 'var(--text)' }}>
                        {item.subtitle}
                      </h3>
                      <p className="text-sm leading-relaxed mb-0" style={{ color: 'var(--muted)' }}>
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Contact Section */}
            <div className="rounded-card border p-8 text-center" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
              <h2 className="font-display text-2xl font-bold mb-4" style={{ color: 'var(--text)', letterSpacing: '-0.01em' }}>
                Questions About Privacy?
              </h2>
              <p className="text-sm mb-6 max-w-md mx-auto leading-relaxed" style={{ color: 'var(--muted)' }}>
                If you have any questions about this Privacy Policy, please contact us.
              </p>
              <a
                href={`mailto:${privacy.contactEmail}`}
                className="btn-pill btn-primary no-underline inline-flex items-center"
              >
                {privacy.contactEmail}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
